import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Check, Copy, ExternalLink, X } from 'lucide-react'

interface RouteLinkModalProps {
  open: boolean
  urls: string[]
  miles: number
  roadCount: number
  onClose: () => void
}

export default function RouteLinkModal({ open, urls, miles, roadCount, onClose }: RouteLinkModalProps) {
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open) setCopiedIdx(null)
  }, [open])

  const copy = async (url: string, idx: number) => {
    try {
      await navigator.clipboard.writeText(url)
    } catch {
      // Clipboard API unavailable (e.g. insecure context) — fall back to a
      // transient textarea, which still works on older mobile browsers.
      const ta = document.createElement('textarea')
      ta.value = url
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
    }
    setCopiedIdx(idx)
    setTimeout(() => setCopiedIdx(null), 2000)
  }

  const single = urls.length === 1

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-3"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ type: 'spring', damping: 26, stiffness: 300 }}
            className="w-full max-w-md rounded-xl bg-[#0C0C0E]/95 backdrop-blur border border-white/10 p-4 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-amber-400">
                  Your route
                </div>
                <div className="text-sm font-semibold text-white">
                  {roadCount} {roadCount === 1 ? 'road' : 'roads'} · {Math.round(miles)} mi
                </div>
              </div>
              <button
                onClick={onClose}
                className="h-9 w-9 flex items-center justify-center rounded-lg text-white/40 hover:text-white"
                title="Close"
              >
                <X size={16} />
              </button>
            </div>

            <p className="text-xs text-white/60 leading-relaxed">
              {single ? (
                <>
                  This opens your route as a turn-by-turn ride in Google Maps, following the roads
                  in the order you built them.
                </>
              ) : (
                <>
                  Google Maps can only handle 10 waypoints per link, so your route has been split
                  up into {urls.length} links. Ride them in order — each one picks up where the
                  previous ends.
                </>
              )}
            </p>

            <div className="space-y-2">
              {urls.map((url, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-amber-400 text-black text-sm font-bold">
                    {i + 1}
                  </span>
                  <button
                    onClick={() => copy(url, i)}
                    className={`flex-1 h-11 flex items-center justify-center gap-2 rounded-lg border text-sm font-medium transition-colors ${
                      copiedIdx === i
                        ? 'border-emerald-400/50 text-emerald-400'
                        : 'border-white/15 text-white/80 hover:border-amber-400/50 hover:text-amber-300'
                    }`}
                  >
                    {copiedIdx === i ? <Check size={15} /> : <Copy size={15} />}
                    {copiedIdx === i ? 'Copied' : 'Copy link'}
                  </button>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 h-11 flex items-center justify-center gap-2 rounded-lg bg-amber-400 hover:bg-amber-300 text-black font-bold text-sm tracking-wide"
                  >
                    <ExternalLink size={15} />
                    Open in Maps
                  </a>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
